import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import LeftNav from './components/Navigation/LeftNav';
import SearchComponent from './components/Search/SearchComponent';
import Tiles from './components/Dashboard/Tiles';
import IframeModal from './components/IframeModal';
import { dashboardTiles } from './data/dashboardData';

// Auth components and context
import { AuthProvider } from './contexts/AuthContext';
import { LoginPage, LoginCallback, ProtectedRoute } from './components/Auth';
import { tokenRefreshService } from './services/tokenRefreshService';
import { logConfigInfo } from './config/config.utils';
import { SubDashboardLink } from './shared/types';
import AnalyticsTracker from './components/AnalyticsTracker';
import { initializeAnalytics } from './services/analyticsService';

// Set up window.dataLayer/gtag before anything else renders or mounts.
initializeAnalytics();

interface DashboardLink {
  name: string;
  url: string;
  tags: string[];
  description?: string;
  subDashboards?: SubDashboardLink[];
}

// Main Dashboard Component (Protected)
const Dashboard: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardLink | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  // Filter tiles based on search query and selected role
  const filteredTiles = useMemo(() => {
    return dashboardTiles.filter(tile => {
      // Apply search filter
      let searchMatch = true;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        
        // Search in tile tags
        const tileTagMatch = tile.tags && tile.tags.some(tag => 
          tag.toLowerCase().includes(query)
        );
        
        // Check if any dashboard matches the search
        const hasMatchingDashboard = tile.dashboards && tile.dashboards.length > 0 && 
          tile.dashboards.some(dashboard => {
            const nameMatch = dashboard.name && 
              dashboard.name.toLowerCase().includes(query);
            const tagMatch = dashboard.tags && dashboard.tags.length > 0 && 
              dashboard.tags.some(tag => tag.toLowerCase().includes(query));
            const hasMatchingSubDashboard = dashboard.subDashboards?.some(subDashboard =>
              subDashboard.name.toLowerCase().includes(query) ||
              subDashboard.description?.toLowerCase().includes(query)
            );
            return nameMatch || tagMatch || hasMatchingSubDashboard;
          });
        
        searchMatch = Boolean(tileTagMatch || hasMatchingDashboard);
      }
      
      // Apply role filter
      let roleMatch = true;
      if (selectedRole) {
        // Check if tile has matching tile tags
        const tileRoleMatch = tile.tags && tile.tags.includes(selectedRole);
        
        // Check if tile has any dashboards with matching role tags
        const hasDashboardWithRole = tile.dashboards && tile.dashboards.length > 0 && 
          tile.dashboards.some(dashboard => 
            dashboard.tags && dashboard.tags.includes(selectedRole)
          );
        
        roleMatch = Boolean(tileRoleMatch || hasDashboardWithRole);
      }
      
      return searchMatch && roleMatch;
    });
  }, [searchQuery, selectedRole]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleRoleSelect = (role: string | null) => {
    setSelectedRole(role);
  };

  const handleResetFilters = () => {
    setSelectedRole(null);
  };

  const handleDashboardClick = (dashboard: DashboardLink) => {
    // Open all dashboard links in new tab
    if (dashboard.url) {
      window.open(dashboard.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDashboard(null);
  };

  return (
    <div className="App">
      <LeftNav 
        selectedRole={selectedRole} 
        onRoleSelect={handleRoleSelect}
        onResetFilters={handleResetFilters}
      />
      <div className="main-content">
        {/* Search component */}
        <div style={{ marginBottom: '1rem' }}>
          <SearchComponent onSearchChange={handleSearchChange} />
        </div>
      </div>
      <Tiles 
        tiles={filteredTiles} 
        onDashboardClick={handleDashboardClick}
        selectedRole={selectedRole}
        searchQuery={searchQuery}
      />
      
      {selectedDashboard && (
        <IframeModal
          isOpen={isModalOpen}
          title={selectedDashboard.name}
          url={selectedDashboard.url || ''}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

// Main App Component with Routing and Authentication
const App: React.FC = () => {
  useEffect(() => {
    // Setup automatic token refresh
    tokenRefreshService.setupAutoRefresh();
    
    // Log configuration info in development
    logConfigInfo();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AnalyticsTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/callback" element={<LoginCallback />} />
          
          {/* Protected Routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
